/**
 * Session continuity — cold start 가 "직전 세션 이어쓰기" 인지 "정식 시작" 인지 구분.
 *
 * 배경:
 *   사용자가 앱을 백그라운드에 두면 OS 또는 WebView 가 메모리 회수 차원에서 페이지를
 *   임의로 unload/kill 한다. 사용자가 다시 앱으로 돌아오면 React 입장에서는 cold start
 *   라서, 정상적으로는 AppSplash 의 2초짜리 애니메이션이 매번 재생된다.
 *
 *   메이저 앱들 (YouTube/Instagram 등) 처럼 "한참 만에 켰는데 splash 가 또 떠?" 라는
 *   어색함을 없애려면, "마지막 활동 시각" 을 영구 저장소에 남겨두고 cold start 시
 *   값이 최근이면 splash 애니메이션을 skip 하면 된다.
 *
 * 그런데 단순히 lastActiveAt 만으로는 부족하다:
 *   - 사용자가 task switcher 로 직접 종료 → 다시 실행한 경우에도
 *     lastActiveAt 이 최근이라 splash 가 skip 됨 (사용자 의도와 어긋남)
 *
 * 해결책: `pagehide(persisted=false)` 이벤트로 "사용자가 의도적으로 종료" 를 감지.
 *   - WebView 가 unload 직전에 발생 (사용자가 task switcher 로 종료 시)
 *   - 일반 backgrounding 에서는 발생 안 함 (또는 persisted=true 로 발생)
 *   - 이 이벤트가 오면 lastActiveAt 을 지워서 다음 시작이 fresh cold start 로 보이게 함
 *
 * 영구 저장 매체는 localStorage — Capacitor WebView 에서도 process kill 을 넘어 유지된다.
 *
 * 본 모듈은 web/native 공용 — localStorage 접근이 차단되면 (시크릿 모드 등) 조용히 false 반환.
 */

const STORAGE_KEY = "template:lastActiveAt";

/** 직전 활동 기록을 "최근" 으로 간주할 기본 임계치 (24h). */
export const WARM_COLD_START_MS = 24 * 60 * 60 * 1000;

/** 현재 시각을 마지막 활동 시점으로 저장. 실패해도 throw 하지 않음. */
export function markActiveNow(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // private mode / quota — splash 동작에는 영향 없음 (skip 만 못 할 뿐)
  }
}

/**
 * 사용자가 의도적으로 앱을 종료한 것으로 보일 때 호출.
 * 다음 cold start 가 warm-resume 이 아닌 fresh-start 로 인지되도록 lastActiveAt 을 비움.
 */
export function clearActiveMarker(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * 직전 세션이 thresholdMs 이내에 활동한 흔적이 있는지.
 * true 면 "이어서 쓰는 중" 으로 간주하고 splash 를 skip.
 */
export function wasRecentlyActive(thresholdMs: number = WARM_COLD_START_MS): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < thresholdMs;
  } catch {
    return false;
  }
}
